import { cva, type VariantProps } from 'class-variance-authority';
import { Slot } from 'radix-ui';
import * as React from 'react';

import { cn } from '@/lib/utils';

const buttonVariants = cva(
  'tap inline-flex select-none items-center justify-center gap-2 whitespace-nowrap rounded-xl text-sm font-medium outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:pointer-events-none disabled:opacity-45 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default:
          'bg-primary text-primary-foreground shadow-[0_1px_0_0_rgba(255,255,255,0.22)_inset,0_8px_24px_-12px_var(--glow)] hover:brightness-105 active:brightness-95',
        secondary:
          'border border-border/70 bg-secondary text-secondary-foreground hover:border-border hover:bg-secondary/80',
        outline:
          'border border-border bg-transparent text-foreground hover:border-foreground/25 hover:bg-muted/60',
        ghost: 'text-foreground/80 hover:bg-muted/70 hover:text-foreground',
        destructive:
          'border border-destructive/25 bg-destructive/10 text-destructive hover:bg-destructive/15',
        link: 'h-auto p-0 text-foreground underline-offset-4 hover:underline',
        brand:
          'border border-white/10 bg-[linear-gradient(105deg,var(--brand)_0%,var(--brand-2)_55%,var(--brand-3)_100%)] text-[#07110d] shadow-[0_10px_40px_-16px_var(--glow)] hover:brightness-[1.06]',
      },
      size: {
        default: 'h-10 px-4',
        sm: 'h-8 gap-1.5 rounded-lg px-3 text-[13px]',
        xs: 'h-7 gap-1 rounded-lg px-2.5 text-xs',
        lg: 'h-12 rounded-xl px-6 text-[15px]',
        icon: 'size-10',
        'icon-sm': 'size-8 rounded-lg',
        'icon-xs': 'size-7 rounded-md',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface ButtonProps
  extends React.ComponentProps<'button'>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

function Button({ className, variant, size, asChild = false, ...props }: ButtonProps) {
  const Component = asChild ? Slot.Root : 'button';
  return (
    <Component
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  );
}

export { Button };
